import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link } from "react-router-dom";
import CreditsBadge from "./CreditsBadge.jsx";
import PiSignInButton from "./PiSignInButton.jsx";
import { useAuth } from "../lib/useAuth";
import { API_BASE } from "../lib/api.js";

const styles = {
  bar: {
    position: "sticky",
    top: 0,
    zIndex: 40,
    background: "#fff",
    borderBottom: "1px solid #e5e7eb",
  },
  wrap: {
    maxWidth: 1120,
    width: "100%",
    margin: "0 auto",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 16px",
    boxSizing: "border-box",
  },
  left: { display: "flex", alignItems: "center", gap: 16 },
  brand: {
    fontSize: 20,
    fontWeight: 700,
    color: "#4f46e5",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  logo: { width: 32, height: 32, objectFit: "contain" },
  nav: { display: "flex", alignItems: "center", gap: 12 },
  link: { padding: "8px 10px", borderRadius: 8, color: "#374151", textDecoration: "none" },
  linkActive: { color: "#4338ca", background: "#eef2ff", fontWeight: 600 },
  right: { display: "flex", alignItems: "center", gap: 12 },
  button: {
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "6px 12px",
    background: "white",
    cursor: "pointer",
  },
  hi: { fontSize: 14, color: "#6b7280" },
};

// ✅ Bell icon component with unread badge
// variant="navbar" = floating dropdown | variant="drawer" = inline expandable (mobile safe)
function NotificationBell({ uid, variant = "navbar" }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Poll for unread count every 30 seconds
  useEffect(() => {
    if (!uid) return;

    const fetchCount = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/notifications`, { credentials: "include" });
        const data = await res.json();
        setUnreadCount(data.unreadCount ?? 0);
        setNotifications(data.items ?? []);
      } catch {}
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [uid]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleOpen = async () => {
    setOpen((prev) => !prev);

    // Mark all as read when opening
    if (!open && unreadCount > 0) {
      try {
        await fetch(`${API_BASE}/api/notifications/read-all`, {
          method: "POST",
          credentials: "include",
        });
        setUnreadCount(0);
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      } catch {}
    }
  };

  const formatTime = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const typeIcon = {
    training_started: "🎨",
    training_complete: "🎉",
    training_failed: "⚠️",
    royalty_earned: "💰",
    style_approved: "✅",
  };

  const bellButton = (
    <button
      onClick={handleOpen}
      style={{
        position: "relative",
        background: "none",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: "6px 10px",
        cursor: "pointer",
        fontSize: 18,
        lineHeight: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background 0.2s",
      }}
      aria-label="Notifications"
    >
      🔔
      {unreadCount > 0 && (
        <span
          style={{
            position: "absolute",
            top: -4,
            right: -4,
            background: "#6D5EF9",
            color: "white",
            borderRadius: "50%",
            width: 18,
            height: 18,
            fontSize: 11,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
          }}
        >
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </button>
  );

  // ── DRAWER variant: inline expandable list, no floating dropdown ──
  if (variant === "drawer") {
    return (
      <div style={{ width: "100%" }}>
        {/* Row: bell + label */}
        <div
          onClick={handleOpen}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 0",
            cursor: "pointer",
            borderBottom: open ? "none" : "1px solid #f3f4f6",
          }}
        >
          <span style={{ position: "relative", fontSize: 20 }}>
            🔔
            {unreadCount > 0 && (
              <span style={{
                position: "absolute", top: -4, right: -6,
                background: "#6D5EF9", color: "white",
                borderRadius: "50%", width: 16, height: 16,
                fontSize: 10, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </span>
          <span style={{ fontSize: 14, color: "#374151", fontWeight: 500 }}>
            Notifications {unreadCount > 0 ? `(${unreadCount})` : ""}
          </span>
          <span style={{ marginLeft: "auto", color: "#9ca3af", fontSize: 12 }}>
            {open ? "▲" : "▼"}
          </span>
        </div>

        {/* Inline notification list */}
        {open && (
          <div style={{
            background: "#fafafa",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            marginBottom: 8,
            maxHeight: 260,
            overflowY: "auto",
          }}>
            {notifications.length === 0 ? (
              <div style={{ padding: "20px 16px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <div key={n.id} style={{
                  padding: "10px 12px",
                  borderBottom: "1px solid #f3f4f6",
                  background: n.read ? "white" : "#f5f3ff",
                }}>
                  <div style={{ display: "flex", gap: 8 }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{typeIcon[n.type] ?? "🔔"}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: n.read ? 500 : 700, fontSize: 13, color: "#111827" }}>
                        {n.title}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{n.message}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>{formatTime(n.createdAt)}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  }

  // ── NAVBAR variant: floating dropdown ──
  return (
    <div style={{ position: "relative" }} ref={dropdownRef}>
      {bellButton}

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 320,
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            zIndex: 100,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid #f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>
              Notifications
            </span>
            {notifications.length > 0 && (
              <span style={{ fontSize: 12, color: "#6b7280" }}>
                {notifications.length} total
              </span>
            )}
          </div>

          {/* Notification list */}
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {notifications.length === 0 ? (
              <div
                style={{
                  padding: "32px 16px",
                  textAlign: "center",
                  color: "#9ca3af",
                  fontSize: 14,
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 8 }}>🔔</div>
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #f9fafb",
                    background: n.read ? "white" : "#f5f3ff",
                    transition: "background 0.2s",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                    }}
                  >
                    <span style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>
                      {typeIcon[n.type] ?? "🔔"}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: n.read ? 500 : 700,
                          fontSize: 13,
                          color: "#111827",
                          marginBottom: 2,
                        }}
                      >
                        {n.title}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "#6b7280",
                          lineHeight: 1.4,
                        }}
                      >
                        {n.message}
                      </div>
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                        {formatTime(n.createdAt)}
                      </div>
                    </div>
                    {!n.read && (
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: "#6D5EF9",
                          flexShrink: 0,
                          marginTop: 4,
                        }}
                      />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, signOut } = useAuth?.() ?? {};
  const [open, setOpen] = useState(false);
  const itemClass = ({ isActive }) => ({
    ...styles.link,
    ...(isActive ? styles.linkActive : null),
  });

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <header style={styles.bar}>
      <div style={styles.wrap}>
        {/* LEFT: Brand + logo + (mobile) hamburger */}
        <div style={styles.left}>
          <Link to="/home" style={styles.brand}>
            <img src="/logo.png" alt="ArtiBytes Logo" style={styles.logo} />
          </Link>

          <button
            className="nav-hamburger"
            aria-label="Open menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
          >
            ☰
          </button>

          <nav className="nav__links" style={styles.nav}>
            <NavLink to="/home" style={itemClass}>Home</NavLink>
            <NavLink to="/create" style={itemClass}>Create</NavLink>
            <NavLink to="/gallery" style={itemClass}>Gallery</NavLink>
            <NavLink to="/pricing" style={itemClass}>Styles</NavLink>
            <NavLink to="/creator" style={itemClass}>Royalty Program</NavLink>
            <NavLink to="/profile" style={itemClass}>Profile</NavLink>
          </nav>
        </div>

        {/* RIGHT: desktop-only actions */}
        <div className="nav__right" style={styles.right}>
          <CreditsBadge />
          {/* ✅ Bell only shown when logged in */}
          {user && <NotificationBell uid={user.uid} />}
          {user ? (
            <>
              <span style={styles.hi}>Hi, {user.username || "@DevUser"}</span>
              <button style={styles.button} onClick={signOut}>Sign out</button>
            </>
          ) : (
            <PiSignInButton />
          )}
        </div>
      </div>

      {/* ===== Mobile drawer + overlay ===== */}
      {open && <div className="nav-overlay" onClick={() => setOpen(false)} />}
      <aside className={`nav-drawer ${open ? "open" : ""}`} aria-hidden={!open}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 16,
            paddingBottom: 12,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <img src="/logo.png" alt="ArtiBytes Logo" style={{ width: 28, height: 28 }} />
          <span style={{ fontSize: 18, fontWeight: 700, color: "#4f46e5" }}>ArtiBytes</span>
        </div>

        <nav className="drawer-nav">
          <NavLink to="/home" onClick={() => setOpen(false)}>Home</NavLink>
          <NavLink to="/create" onClick={() => setOpen(false)}>Create</NavLink>
          <NavLink to="/gallery" onClick={() => setOpen(false)}>Gallery</NavLink>
          <NavLink to="/pricing" onClick={() => setOpen(false)}>Styles</NavLink>
          <NavLink to="/creator" onClick={() => setOpen(false)}>Royalty Program</NavLink>
          <NavLink to="/profile" onClick={() => setOpen(false)}>Profile</NavLink>
          {/* ✅ Bell as inline nav item — expands in place, no off-screen dropdown */}
          {user && <NotificationBell uid={user.uid} variant="drawer" />}
        </nav>

        <div className="drawer-footer">
          <CreditsBadge />
          {user ? (
            <>
              <span style={{ fontSize: 13, color: "#6b7280" }}>Hi, {user.username || "@DevUser"}</span>
              <button
                className="drawer-signout"
                onClick={() => {
                  setOpen(false);
                  signOut?.();
                }}
              >
                Sign out
              </button>
            </>
          ) : (
            <PiSignInButton />
          )}
        </div>
      </aside>
    </header>
  );
}